import React, { useEffect, useState } from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Divider from "@mui/joy/Divider";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Stack from "@mui/joy/Stack";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Typography from "@mui/joy/Typography";
import Card from "@mui/joy/Card";
import CardActions from "@mui/joy/CardActions";
import CardOverflow from "@mui/joy/CardOverflow";
import { db, storage } from "../../FirebaseConfig";
import firebase from "firebase/compat/app";
import { Link, useParams } from "react-router-dom"; // Import useParams for fetching ID from URL
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import Radio from "@mui/joy/Radio";
import RadioGroup from "@mui/joy/RadioGroup";
import IconButton from "@mui/joy/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from '@mui/icons-material/Add';

const initialState = {
  id: "",
  name: "",
  description: "",
  hints: [] as string[],
  flag: "",
  category: "",
  difficulty: "",
  points: "",
  fileUrl: "",
  live: true,
};

export default function EditChallenge() {
  const [data, setData] = useState(initialState);
  const [fileLoading, setFileLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadFile, setUploadFile] = useState("");
  const [inputType, setInputType] = useState<"file" | "url">("url");
  const { id } = useParams<{ id: string }>(); // Get challenge ID from URL

  // Fetch the challenge data
  useEffect(() => {
    if (id) {
      db.collection("ctf")
        .doc(id)
        .get()
        .then((doc) => {
          if (doc.exists) {
            const challengeData = doc.data();
            setData((prevData) => ({
              ...prevData,
              ...challengeData,
            }));
            setUploadFile(challengeData?.fileUrl || ""); // Set file URL if exists
          }
        })
        .catch((error) => {
          toast.error("Failed to load challenge data");
        });
    }
  }, [id]);

  const handleChange = (e: any) => {
    if (!e || !e.target) return;
    const { id, value } = e.target;
    setData((prevData) => ({ ...prevData, [id]: value }));
  };

  const handleCatSelectChange = (event: React.SyntheticEvent | null, newValue: string | null) => {
    if (newValue) setData((prevData) => ({ ...prevData, category: newValue }));
  };

  const handleDiffSelectChange = (event: React.SyntheticEvent | null, newValue: string | null) => {
    if (newValue) setData((prevData) => ({ ...prevData, difficulty: newValue }));
  };

  const handlePostUrl = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUploadFile(event.target.value);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputType(event.target.value as "file" | "url");
  };

  const handlePostFile = async (e: any) => {
    setFileLoading(true);
    const file = e.target.files[0];
    const fileRef = storage.ref().child(`ctf-files/${file.name}`);
    const uploadTask = fileRef.put(file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(progress);
      },
      (error) => {
        toast.error("Upload failed!");
        setFileLoading(false);
      },
      () => {
        uploadTask.snapshot.ref.getDownloadURL().then((downloadURL) => {
          setFileLoading(false);
          setUploadFile(downloadURL);
          toast.success("File Uploaded Successfully");
        });
      }
    );
  };

  const validateForm = () => {
    return (
      data.name &&
      data.description &&
      data.hints.length > 0 &&
      data.flag &&
      data.category &&
      data.difficulty &&
      data.points &&
      !fileLoading &&
      (inputType === "url" || uploadFile)
    );
  };

  const onSubmit = (e: any) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill out all fields and upload a file or provide a URL.");
      return;
    }

    data.fileUrl = uploadFile;
    const challengeDoc = db.collection("ctf").doc(id || uuidv4());

    challengeDoc
      .set(
        {
          id: id || uuidv4(),
          name: data.name,
          description: data.description,
          hints: data.hints,
          flag: data.flag,
          category: data.category,
          difficulty: data.difficulty,
          points: data.points,
          fileUrl: uploadFile,
          createdAt: id ? firebase.firestore.FieldValue.serverTimestamp() : firebase.firestore.FieldValue.serverTimestamp(),
          live: data.live,
        },
        { merge: true }
      )
      .then(() => {
        toast.success("CTF Challenge Updated Successfully");
        setTimeout(() => window.location.reload(), 1000);
      })
      .catch(() => {
        toast.error("Error updating Challenge");
      });
  };

  const addHint = () => {
    setData((prevData) => ({ ...prevData, hints: [...prevData.hints, ""] }));
  };

  const handleHintChange = (index: number, value: string) => {
    setData((prevData) => {
      const newHints = [...prevData.hints];
      newHints[index] = value;
      return { ...prevData, hints: newHints };
    });
  };

  const removeHint = (index: number) => {
    setData((prevData) => {
      const newHints = [...prevData.hints];
      newHints.splice(index, 1);
      return { ...prevData, hints: newHints };
    });
  };

  return (
    <Box sx={{ flex: 1, width: "100%" }}>
      <Stack
        spacing={4}
        sx={{
          display: "flex",
          mx: "auto",
          py: { xs: 2, md: 3 },
        }}
      >
        <Card>
          <Box sx={{ mb: 1 }}>
            <Typography level="title-md">Edit Challenge</Typography>
            <Typography level="body-sm">Edit the required fields</Typography>
          </Box>
          <Divider />
          <Stack
            direction="row"
            spacing={3}
            sx={{ display: { xs: "none", md: "flex" }, my: 1 }}
          >
            <Stack spacing={2} sx={{ flexGrow: 1 }}>
              <FormLabel>Challenge Name</FormLabel>
              <Input
                size="sm"
                id="name"
                onChange={handleChange}
                value={data.name}
                placeholder="Name of the Challenge"
                required
              />
  
              <FormLabel>Challenge Description</FormLabel>
              <Input
                size="sm"
                id="description"
                onChange={handleChange}
                value={data.description}
                placeholder="Description of the Challenge"
                required
              />
  
              <FormLabel>Hints</FormLabel>
              {data.hints.map((hint, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                  <Input
                    size="sm"
                    value={hint}
                    onChange={(e) => handleHintChange(index, e.target.value)}
                    placeholder={`Hint ${index + 1}`}
                    required
                    sx={{ flexGrow: 1, mr: 1 }}
                  />
                  <IconButton
                    onClick={() => removeHint(index)}
                    variant="outlined"
                    color="danger"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                onClick={addHint}
                variant="outlined"
                startDecorator={<AddIcon />}
                color="primary"
                sx={{ mt: 1 }}
              >
                Add Hint
              </Button>
  
              <FormLabel>Flag</FormLabel>
              <Input
                size="sm"
                id="flag"
                onChange={handleChange}
                value={data.flag}
                placeholder="Flag of the Challenge"
                required
              />
  
              <FormLabel>Categories</FormLabel>
              <Select
                id="category"
                size="sm"
                onChange={handleCatSelectChange}
                value={data.category}
                placeholder="Choose the Category of Challenge"
                required
              >
                <Option value="Web Exploitation">Web Exploitation</Option>
                <Option value="Digital Forensics">Digital Forensics</Option>
                <Option value="Cryptography">Cryptography</Option>
                <Option value="Steganography">Steganography</Option>
                <Option value="Reverse Engineering">Reverse Engineering</Option>
                <Option value="Miscellaneous">Miscellaneous</Option>
                <Option value="OSINT">OSINT</Option>
                <Option value="Binary Exploitation">Binary Exploitation</Option>
                <Option value="Networking">Networking</Option>
                <Option value="OT">OT</Option>
              </Select>
  
              <FormLabel>Difficulty</FormLabel>
              <Select
                id="difficulty"
                size="sm"
                onChange={handleDiffSelectChange}
                value={data.difficulty}
                placeholder="Choose the Difficulty of Challenge"
                required
              >
                <Option value="Easy">Easy</Option>
                <Option value="Moderate">Moderate</Option>
                <Option value="Hard">Hard</Option>
              </Select>
  
              <FormLabel>Points</FormLabel>
              <Input
                size="sm"
                id="points"
                onChange={handleChange}
                value={data.points}
                placeholder="Points of the Challenge"
                required
              />
  
              <Stack spacing={2} sx={{ my: 1 }}>
                <FormLabel>Challenge File Type</FormLabel>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, justifyContent: "center" }}>
                  <RadioGroup
                    orientation="horizontal"
                    aria-labelledby="file-type-radio"
                    name="inputType"
                    value={inputType}
                    onChange={handleInputChange}
                    sx={{
                      minHeight: 48,
                      padding: "4px",
                      borderRadius: "12px",
                      bgcolor: "neutral.softBg",
                      "--RadioGroup-gap": "4px",
                      "--Radio-actionRadius": "8px",
                    }}
                  >
                    {["file", "url"].map((item) => (
                      <Radio
                        key={item}
                        color="neutral"
                        value={item}
                        disableIcon
                        label={item}
                        variant="plain"
                        sx={{ px: 2, alignItems: "center" }}
                        slotProps={{
                          action: ({ checked }) => ({
                            sx: {
                              ...(checked && {
                                bgcolor: "background.surface",
                                boxShadow: "sm",
                                "&:hover": { bgcolor: "background.surface" },
                              }),
                            },
                          }),
                        }}
                      />
                    ))}
                  </RadioGroup>
                </Box>
                {inputType === "file" ? (
                  <>
                    <FormLabel>Upload Challenge File</FormLabel>
                    <Input
                      size="sm"
                      type="file"
                      onChange={handlePostFile}
                      required={!uploadFile} // only required if no file URL is provided
                    />
                  </>
                ) : (
                  <>
                    <FormLabel>Post URL for the Challenge File</FormLabel>
                    <Input
                      size="sm"
                      onChange={handlePostUrl}
                      value={uploadFile}
                      placeholder="URL for the Challenge File"
                      required={!uploadFile} // only required if no file is uploaded
                    />
                  </>
                )}
              </Stack>
            </Stack>
          </Stack>
          {fileLoading ? <Typography level="body-sm">Uploading File {progress.toFixed(2)} %</Typography> : null}
          <CardOverflow sx={{ borderTop: "1px solid", borderColor: "divider" }}>
            <CardActions sx={{ alignSelf: "flex-end", pt: 2 }}>
              <Button
                component={Link}
                to="/challenges"
                size="sm"
                variant="outlined"
                color="neutral"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="outlined"
                onClick={onSubmit}
                disabled={!validateForm()}
              >
                Save
              </Button>
            </CardActions>
          </CardOverflow>
        </Card>
      </Stack>
    </Box>
  );
}
